/**
 * Carrier MCP stdio — the shared "this tool needs the Worker" reply.
 *
 * Not every `ui_agent_*` tool can run in a CLI, and two groups genuinely cannot
 * for reasons that are structural rather than unfinished work:
 *
 * THE OCS DASHBOARD TOOLS (`ui_create_steering_list` and the nine beside it)
 *   need OCS portal login credentials to drive the dashboard. Those live in
 *   Clerk `privateMetadata` and are fetched through the Clerk Backend API with
 *   `CLERK_SECRET_KEY` and an org id. The stdio shim does not carry either,
 *   and `props` has no `org_id`, so the Worker Clerk lookup never runs here.
 *
 *   That is no longer the whole story. A CLI operator who wants these can
 *   supply the login directly, behind the two-part opt-in in
 *   `portal-credentials.ts`. This reply is what they get until they do, and it
 *   is byte-identical across every incomplete state of that opt-in — a refusal
 *   that named the missing half would let a caller enumerate the operator's
 *   configuration one variable at a time. Read as a genuine structural miss, it
 *   is still true: without the opt-in there is no credential here.
 *
 * THE SCHEDULE TOOLS (`ui_agent_schedule_*`)
 *   need a recurring runtime. Neither Steel nor Browser Use Cloud has a native
 *   scheduler — `ui-agent-schedule.ts` records that Manus did and that the
 *   Worker had to rebuild it on CF KV plus the `scheduled` export on a
 *   `*​/5 * * * *` Cron Trigger, with KV leader election so multiple instances
 *   do not double-fire. A CLI process that exits when the client disconnects
 *   has no cron, no leader to elect and nothing to elect it with. A schedule
 *   accepted here would be silently dropped, which is worse than a refusal.
 *
 * Registration still happens for both groups, deliberately. `generate:docs`,
 * `verify:fidelity` and `catalog-parity.test.js` compare the two surfaces by
 * registered tool name, so dropping the registrations would read as catalog
 * drift rather than as the honest statement that this transport cannot serve
 * them.
 *
 * The message names no vendor. The text these replaced said "Manus" in one file
 * and "Steel" in another — two and one vendors stale respectively.
 */

export type ToolResult = {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
};

export const REMOTE_MCP_URL = "https://mcp.carrier.llc/mcp";

/** Why a given tool cannot run here. Kept distinct so the reply is actionable. */
export type StubReason = "ocs_portal_credentials" | "recurring_runtime";

const REASON_TEXT: Record<StubReason, string> = {
  ocs_portal_credentials:
    "drives the OCS web dashboard, which needs OCS portal login credentials. " +
    "Those are stored in Clerk private metadata and read through the Clerk Backend API, " +
    "which the local CLI has no secret for.",
  recurring_runtime:
    "needs a recurring runtime. Browser Use Cloud has no native scheduler, so the hosted " +
    "Worker implements schedules with KV plus a Cloudflare Cron Trigger and leader election. " +
    "A CLI process exits when the client disconnects, so a schedule created here would never fire.",
};

/**
 * The reply a stubbed ui-agent tool returns.
 *
 * `toolName` is interpolated so an agent reading the error knows which call it
 * was, and `reason` distinguishes "needs a credential you could supply" from
 * "cannot exist in this process model" — those point at different next steps.
 */
export function uiAgentStubError(
  toolName: string,
  reason: StubReason,
): ToolResult {
  return {
    isError: true,
    content: [
      {
        type: "text" as const,
        text: JSON.stringify({
          error: "requires_worker_runtime",
          tool: toolName,
          reason,
          message:
            `${toolName} ${REASON_TEXT[reason]} ` +
            "Connect to the remote Carrier MCP Worker to use this tool. " +
            "Generic web browsing (ui_agent_ask / ui_agent_status) does work locally — " +
            "set BROWSER_USE_API_KEY.",
          remote_url: REMOTE_MCP_URL,
        }),
      },
    ],
  };
}
